import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Trash2, Download, Film, Image as ImageIcon, Sparkles, Maximize2, Tag } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useShell } from '../components/AppShell';
import ListingCopy from '../components/ListingCopy';

// What this instance made, as opposed to what was uploaded to it. The Asset
// Library holds the creator's own photographs; this holds the output, and the
// two were worth separating because only one of them can be made again.
const TABS = [
  { key: 'all', label: 'Everything' },
  { key: 'image', label: 'Images' },
  { key: 'video', label: 'Videos' },
];

export default function Creations() {
  // The empty state opens the photo-session catalogue directly instead of
  // dropping the person back on the dashboard to find the same button.
  const { openPhotoSessions } = useShell();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [writing, setWriting] = useState(null);
  // Pagination: 40 at a time instead of every video element at once. skip
  // counts RAW rows fetched (before the succeeded/url filter), because that
  // is the offset the server understands.
  const [rawCount, setRawCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE = 40;

  const load = useCallback(() => {
    api.listGenerationsPage(0, PAGE)
      .then((r) => {
        const rows = r || [];
        setRawCount(rows.length);
        setHasMore(rows.length === PAGE);
        setItems(rows.filter((g) => g.status === 'succeeded'
                                    && (g.url || g.video_url)));
      })
      .catch((e) => toast({ title: 'Could not load your creations', description: e.message }))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const rows = (await api.listGenerationsPage(rawCount, PAGE)) || [];
      setRawCount((n) => n + rows.length);
      setHasMore(rows.length === PAGE);
      const ok = rows.filter((g) => g.status === 'succeeded' && (g.url || g.video_url));
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        return [...prev, ...ok.filter((g) => !seen.has(g.id))];
      });
    } catch (e) {
      toast({ title: 'Could not load more', description: e.message });
    } finally { setLoadingMore(false); }
  };

  const shown = useMemo(() => items.filter((g) => {
    if (tab === 'video') return !!g.video_url;
    if (tab === 'image') return !g.video_url;
    return true;
  }), [items, tab]);

  // Its own creation rather than a replacement: the small one is what the
  // workflow and every thumbnail point at, and nothing is charged because this
  // runs on the machine that serves the file.
  const enlarge = async (g) => {
    setBusyId(g.id);
    try {
      const r = await api.upscaleGeneration(g.id, { target: 'amazon' });
      load();
      toast({ title: `Enlarged to ${r.width} × ${r.height}`,
              description: 'Saved as a new creation. Big enough for a marketplace listing.' });
    } catch (e) {
      toast({ title: 'Could not enlarge it', description: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (g) => {
    const what = g.video_url ? 'video' : 'image';
    if (!window.confirm(`Delete this ${what}? It cannot be recovered.`)) return;
    setBusyId(g.id);
    try {
      await api.deleteGeneration(g.id);
      setItems((prev) => prev.filter((x) => x.id !== g.id));
      if (preview?.id === g.id) setPreview(null);
    } catch (e) {
      toast({ title: 'Could not delete it', description: e.message });
    } finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-[#0E0E10] text-white">
      <main className="mx-auto max-w-6xl px-6 py-10">
        <h1 className="font-serif-display text-3xl">Your creations</h1>
        <p className="mt-1 text-sm text-white/50">
          Everything this studio has made for you. They stay here after the session
          that made them is gone.
        </p>

        <div className="mt-6 flex gap-2">
          {TABS.map((t) => {
            const n = t.key === 'all' ? items.length
              : items.filter((g) => (t.key === 'video' ? !!g.video_url : !g.video_url)).length;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`creations-tab-${t.key}`}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
                  tab === t.key ? 'bg-white text-black' : 'border border-[#2A2A30] text-white/60 hover:text-white'}`}>
                {t.label} <span className="opacity-50">{n}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
        ) : shown.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-[#2A2A30] p-12 text-center">
            <Sparkles size={20} className="mx-auto text-white/25" />
            <p className="mt-3 text-white/50">Nothing here yet.</p>
            <button onClick={openPhotoSessions} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-medium text-black">
              Make something
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {shown.map((g, i) => {
              const isVideo = !!g.video_url;
              const src = fileUrl(g.url || g.video_url);
              // Dozens of eager <video> elements each open a connection just to
              // paint a frame. With a poster (the still the clip was made from)
              // nothing loads until play; without one, only the first rows
              // fetch metadata for their thumbnail.
              const poster = isVideo && g.url ? fileUrl(g.url) : undefined;
              const vidPreload = poster ? 'none' : (i < 12 ? 'metadata' : 'none');
              return (
                <div key={g.id} className="group relative overflow-hidden rounded-xl border border-[#2A2A30] bg-[#141417]">
                  <button onClick={() => setPreview(g)} className="block w-full">
                    <div className="relative aspect-[3/4] bg-[#0E0E10]">
                      {isVideo ? (
                        <video src={fileUrl(g.video_url)} className="h-full w-full object-cover" muted playsInline preload={vidPreload} poster={poster} />
                      ) : (
                        <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                      )}
                      <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/65 px-2 py-0.5 text-[10px] text-white/80">
                        {isVideo ? <Film size={10} /> : <ImageIcon size={10} />} {isVideo ? 'video' : 'image'}
                      </span>
                    </div>
                  </button>
                  <div className="flex items-center justify-between gap-1 px-2.5 py-2">
                    {/* The renderer is stated because it is now recorded, and
                        because "which model made this" is a fair question. */}
                    <span className="truncate text-[10px] text-white/35" title={g.model_slug || ''}>
                      {g.provider || 'unknown'}
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5">
                      {/* Every renderer here stops near one megapixel, and a
                          marketplace rejects a main image under 1600 pixels, so
                          the photograph is finished and still too small to
                          list. This is the step between the two. */}
                      {!isVideo && !g.upscale_of && (
                        <button onClick={() => enlarge(g)} disabled={busyId === g.id}
                          data-testid={`creation-upscale-${g.id}`}
                          title="Make it big enough to list or print"
                          className="flex h-6 w-6 items-center justify-center rounded text-white/35 hover:bg-white/5 hover:text-white disabled:opacity-50">
                          {busyId === g.id ? <Loader2 size={12} className="animate-spin" /> : <Maximize2 size={12} />}
                        </button>
                      )}
                      {/* Al lado de ampliar a proposito: son los dos pasos que
                          quedan entre una fotografia terminada y un anuncio
                          publicado, y el vendedor ya esta mirando aqui. */}
                      {!isVideo && (
                        <button onClick={() => setWriting(g)}
                          data-testid={`creation-listing-${g.id}`}
                          title="Write the listing from this photograph"
                          className="flex h-6 w-6 items-center justify-center rounded text-white/35 hover:bg-white/5 hover:text-white">
                          <Tag size={12} />
                        </button>
                      )}
                      <a href={fileUrl(g.video_url || g.url)} download
                        className="flex h-6 w-6 items-center justify-center rounded text-white/35 hover:bg-white/5 hover:text-white"
                        title="Download">
                        <Download size={12} />
                      </a>
                      <button onClick={() => remove(g)} disabled={busyId === g.id}
                        data-testid={`creation-delete-${g.id}`} title="Delete"
                        className="flex h-6 w-6 items-center justify-center rounded text-white/35 hover:bg-red-500/15 hover:text-red-300 disabled:opacity-50">
                        {busyId === g.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </button>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && hasMore && (
          <div className="mt-8 flex justify-center">
            <button onClick={loadMore} disabled={loadingMore} data-testid="creations-load-more"
              className="inline-flex items-center gap-2 rounded-full border border-[#2A2A30] px-5 py-2 text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white disabled:opacity-50">
              {loadingMore && <Loader2 size={14} className="animate-spin" />} Load more
            </button>
          </div>
        )}
      </main>

      {writing && (
        <ListingCopy generation={writing} onClose={() => setWriting(null)} />
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-6" onClick={() => setPreview(null)}>
          {preview.video_url
            ? <video src={fileUrl(preview.video_url)} controls autoPlay className="max-h-[88vh] max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />
            : <img src={fileUrl(preview.url)} alt="" className="max-h-[88vh] max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />}
        </div>
      )}
    </div>
  );
}
